import {MANAGE_BUDGET_URL, UPGRADE_PLAN_URL_NEW} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {UserLevelBudgetState} from '@github-ui/copilot-chat-types'
import {EntitlementContext} from '@github-ui/copilot-entitlement/EntitlementContext'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {ArrowUpRightIcon, InfoIcon, PencilIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton, Label, Link, LinkButton, ProgressBar, Stack, Text} from '@primer/react'
import {use, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {formatQuotaResetLabel} from '../hooks/use-quota-banner'
import {useSafeChatState} from '../utils/CopilotChatContext'
import {calculateTurnCredits, normalizeTokenPrices} from '../utils/model-pricing'
import styles from './TokenUsageInfo.module.css'

export interface TokenUsageInfoData {
  includedCredits: {
    percentUsed: number
    isExceeded: boolean
    resetLabel: string | null
    total: number | null
    used: number | null
  }
  additionalUsage: {
    enabled: boolean
    percentUsed: number
    isExceeded: boolean
    budgetAmount: number | null
    budgetSpend: number | null
  } | null
  monthlyLimit: {
    state: UserLevelBudgetState | null
    percentUsed: number
    subtitleLabel: string | null
    creditsUsed: number | null
    creditsTotal: number | null
    // Budget-scoped credits used (premiumInteractionsQuota.used), matches percentUsed/percentRemaining.
    used: number | null
  } | null
  isUserLevelBudget: boolean
  canUpgradePlan: boolean
}

export interface SessionTokenUsage {
  inputTokens: number
  outputTokens: number
  totalCredits: number | null
}

const numberFormatter = new Intl.NumberFormat('en-US')
const currencyFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'})
const upgradeAnalyticsEvent = {
  category: 'COPILOT_CHAT',
  action: 'upgrade_plan:token_usage_indicator',
  label: 'ref_cta:upgrade;ref_loc:token_usage_indicator;',
} as const

function formatNumber(amount: number | null): string {
  if (amount === null) return '-'
  return numberFormatter.format(amount)
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return '-'
  return currencyFormatter.format(amount)
}

function headerClassName(isTop: boolean): string {
  return isTop ? `${styles.tokenUsageSectionHeader} ${styles.tokenUsageTopHeader}` : styles.tokenUsageSectionHeader
}

function UsageHintPopover({title, description}: {title: string; description: string}) {
  const [isOpen, setIsOpen] = useState(false)
  const overlayId = useId()
  const overlayRef = useRef<HTMLDivElement>(null)
  const announceRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    overlayRef.current?.focus()

    const announceText = `${title}. ${description}`
    const timeout = setTimeout(() => {
      if (announceRef.current) {
        announceRef.current.textContent = announceText
      }
    }, 50)

    return () => clearTimeout(timeout)
  }, [isOpen, title, description])

  return (
    <>
      <AnchoredOverlay
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderAnchor={props => (
          <IconButton
            size="small"
            variant="invisible"
            icon={InfoIcon}
            {...props}
            aria-haspopup={undefined}
            aria-expanded={props['aria-expanded']}
            aria-label={`Learn more about ${title}`}
            aria-labelledby={undefined}
          />
        )}
        side="outside-top"
        align="center"
        width="medium"
      >
        <Stack
          ref={overlayRef}
          id={overlayId}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${overlayId}-title`}
          aria-describedby={`${overlayId}-title ${overlayId}-description`}
          direction="vertical"
          gap="normal"
          padding="spacious"
        >
          <Text id={`${overlayId}-title`} size="medium" weight="semibold">
            {title}
          </Text>
          <Text id={`${overlayId}-description`} size="medium">
            {description}
          </Text>
        </Stack>
      </AnchoredOverlay>
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

function UpgradePlanLink() {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const handleClick = useCallback(() => {
    sendClickAnalyticsEvent(upgradeAnalyticsEvent)
  }, [sendClickAnalyticsEvent])

  return (
    <LinkButton
      href={UPGRADE_PLAN_URL_NEW}
      variant="primary"
      block
      aria-label="Upgrade Copilot plan"
      onClick={handleClick}
    >
      Upgrade
    </LinkButton>
  )
}

function useSessionTokenUsage(): SessionTokenUsage | null {
  const chatState = useSafeChatState()
  const messages = chatState?.messages
  const availableModels = chatState?.availableModels

  return useMemo(() => {
    if (!messages) return null
    let inputTokens = 0
    let outputTokens = 0
    let totalCredits = 0
    let hasUsage = false
    let hasPricing = false
    const modelsById = availableModels ? new Map(availableModels.map(m => [m.id, m])) : undefined
    for (const msg of messages) {
      if (msg.role === 'assistant' && msg.usage) {
        inputTokens += msg.usage.inputTokens
        outputTokens += msg.usage.outputTokens
        hasUsage = true
        const model = msg.model ? modelsById?.get(msg.model) : undefined
        const pricing = normalizeTokenPrices(model?.billing?.token_prices)
        if (pricing) {
          totalCredits += calculateTurnCredits(msg.usage, pricing)
          hasPricing = true
        }
      }
    }
    return hasUsage
      ? {inputTokens, outputTokens, totalCredits: hasPricing ? Math.round(totalCredits * 100) / 100 : null}
      : null
  }, [messages, availableModels])
}

export function useTokenUsageInfo({required = true}: {required?: boolean} = {}): TokenUsageInfoData | null {
  const entitlement = use(EntitlementContext)
  if (!entitlement) {
    if (required) throw new Error('useTokenUsageInfo must be used within EntitlementProvider')
    return null
  }

  const {
    isIndividualPaidOrStudentPlan,
    overagesRemaining,
    overagesExceeded,
    overagesEnabled,
    overagesBudgetAmount,
    overagesBudgetSpend,
    quotaExceeded,
    quotaRemaining,
    quotaTotalAmount,
    quotaUsedAmount,
    quotaResetAt,
    licenseProvisioning,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    creditsUsed,
    canUpgradePlan,
  } = entitlement

  // Don't show while the license is still being provisioned to avoid misleading states.
  if (licenseProvisioning) return null

  // No quota data available yet.
  if (quotaRemaining === undefined) return null

  const percentUsed = Math.max(0, Math.min(100, 100 - quotaRemaining))
  const resetLabel = formatQuotaResetLabel(quotaResetAt)

  const showAdditionalUsage = isIndividualPaidOrStudentPlan
  const overagePercentUsed = overagesRemaining !== undefined ? Math.max(0, Math.min(100, 100 - overagesRemaining)) : 0

  const monthlyLimitPercentUsed =
    userLevelBudgetPercentRemaining !== null ? Math.max(0, Math.min(100, 100 - userLevelBudgetPercentRemaining)) : 0

  const monthlySubtitleLabel =
    userLevelBudgetState === UserLevelBudgetState.NoLimit
      ? 'You have no monthly limit on AI credits usage set by your organization.'
      : resetLabel
        ? `Resets ${resetLabel}`
        : null

  return {
    includedCredits: {
      percentUsed,
      isExceeded: quotaExceeded,
      resetLabel,
      total: quotaTotalAmount ?? null,
      used: quotaUsedAmount ?? null,
    },
    additionalUsage: showAdditionalUsage
      ? {
          enabled: overagesEnabled,
          percentUsed: overagePercentUsed,
          isExceeded: overagesExceeded,
          budgetAmount: overagesBudgetAmount ?? null,
          budgetSpend: overagesBudgetSpend ?? null,
        }
      : null,
    monthlyLimit: userLevelBudgetEnabled
      ? {
          state: userLevelBudgetState,
          percentUsed: monthlyLimitPercentUsed,
          subtitleLabel: monthlySubtitleLabel,
          creditsUsed: creditsUsed ?? null,
          creditsTotal: quotaTotalAmount ?? null,
          used: quotaUsedAmount ?? null,
        }
      : null,
    isUserLevelBudget: userLevelBudgetEnabled,
    canUpgradePlan,
  }
}

/**
 * Displays quota and session token usage information.
 * Quota data comes from the entitlement context. Session token usage is read from
 * the surrounding chat state when present; outside a `CopilotChatProvider` the
 * session usage section is simply omitted.
 */
export function TokenUsageInfo() {
  const data = useTokenUsageInfo()
  const sessionUsage = useSessionTokenUsage()

  return <TokenUsageInfoContent data={data} sessionUsage={sessionUsage} />
}

export function TokenUsageInfoContent({
  data,
  sessionUsage,
}: {
  data: TokenUsageInfoData | null
  sessionUsage: SessionTokenUsage | null
}) {
  if (!data && !sessionUsage) return null
  if (!data) {
    // Only session token usage available (no quota data)
    if (!sessionUsage) return null
    return (
      <div className={styles.narrowWrapper}>
        <SessionTokenUsageSection sessionUsage={sessionUsage} showDivider={false} isTopSection />
      </div>
    )
  }

  const {includedCredits, additionalUsage, monthlyLimit, isUserLevelBudget, canUpgradePlan} = data
  const includedCreditsProgressBarColor = includedCredits.percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'
  const additionalUsageProgressBarColor =
    (additionalUsage?.percentUsed ?? 0) >= 75 ? 'severe.emphasis' : 'accent.emphasis'
  const showsIncludedCredits = !isUserLevelBudget
  const showsAdditionalUsage = Boolean(additionalUsage)
  const showsMonthlyLimit = Boolean(monthlyLimit)

  return (
    <div className={styles.narrowWrapper}>
      {showsIncludedCredits && (
        <div className={styles.tokenUsageSection}>
          <div className={headerClassName(true)}>
            <span className={styles.tokenUsageSectionTitle}>
              Included credits
              <UsageHintPopover
                title="Included credits"
                description="AI credits included with your plan, reset monthly. Enable additional usage to continue with pay-as-you-go credits once you run out of your included usage."
              />
            </span>
          </div>
          <div className={styles.tokenUsageDetails}>
            <span className={styles.tokenUsageDetailMuted}>
              {includedCredits.resetLabel ? (
                <>
                  Resets {includedCredits.resetLabel}
                  <br />
                  Refresh for latest usage
                </>
              ) : null}
            </span>
            {includedCredits.total != null && includedCredits.used != null ? (
              <span className={styles.tokenUsageDetail}>
                {formatNumber(includedCredits.used)}
                <span className={styles.tokenUsageDetailMuted}>
                  {' '}
                  / {formatNumber(includedCredits.total)} AI credits
                </span>
              </span>
            ) : null}
          </div>
          <Stack direction="vertical" gap="normal">
            <ProgressBar barSize="small">
              <ProgressBar.Item
                progress={includedCredits.percentUsed}
                bg={includedCreditsProgressBarColor}
                aria-label={`Included credits: ${includedCredits.percentUsed}% used`}
                data-progress-bar-bg={includedCreditsProgressBarColor}
              />
            </ProgressBar>
            {canUpgradePlan && <UpgradePlanLink />}
          </Stack>
        </div>
      )}

      {additionalUsage && (
        <>
          <div className={styles.tokenUsageDivider} />
          <div className={styles.tokenUsageSection}>
            <div className={headerClassName(!showsIncludedCredits)}>
              <span className={styles.tokenUsageSectionTitle}>
                Additional usage
                <UsageHintPopover
                  title="Additional usage"
                  description="Pay-as-you-go usage of additional AI credits once you run out of your included usage. Set a budget to cap your maximum monthly spend."
                />
              </span>
              <Link href={MANAGE_BUDGET_URL} className={styles.editBudgetLink}>
                <PencilIcon size={12} />
                Edit budget
              </Link>
            </div>
            <div className={styles.tokenUsageDetails}>
              <span className={styles.tokenUsageDetailMuted}>
                {additionalUsage.isExceeded ? 'Exceeded' : additionalUsage.enabled ? 'Enabled' : 'Disabled'}
              </span>
              {additionalUsage.budgetAmount != null && additionalUsage.budgetSpend != null ? (
                <span
                  className={styles.tokenUsageDetail}
                >{`${formatCurrency(additionalUsage.budgetSpend)} / ${formatCurrency(additionalUsage.budgetAmount)}`}</span>
              ) : null}
            </div>
            <ProgressBar
              progress={additionalUsage.percentUsed}
              bg={additionalUsageProgressBarColor}
              barSize="small"
              aria-label={`Additional usage: ${additionalUsage.percentUsed}% used`}
            />
          </div>
        </>
      )}

      {monthlyLimit && (
        <MonthlyLimitSection
          monthlyLimit={monthlyLimit}
          showDivider={!isUserLevelBudget || !!additionalUsage}
          isTopSection={!showsIncludedCredits && !showsAdditionalUsage}
        />
      )}
      {sessionUsage && (
        <SessionTokenUsageSection
          sessionUsage={sessionUsage}
          showDivider
          isTopSection={!showsIncludedCredits && !showsAdditionalUsage && !showsMonthlyLimit}
        />
      )}
    </div>
  )
}

function MonthlyLimitSection({
  monthlyLimit,
  showDivider,
  isTopSection,
}: {
  monthlyLimit: NonNullable<TokenUsageInfoData['monthlyLimit']>
  showDivider: boolean
  isTopSection: boolean
}) {
  const {state, percentUsed, subtitleLabel, creditsUsed, creditsTotal, used} = monthlyLimit

  // NoLimit and UsageBlocked have no numeric limit, so they render "X AI credits used" with no
  // progress bar. All other states are budget states that render a bar and "X / Y AI credits".
  const isUnlimitedFamily = state === UserLevelBudgetState.NoLimit || state === UserLevelBudgetState.UsageBlocked

  const hasBudgetTotal = creditsTotal !== null && creditsTotal > 0

  // Budget states show budget-scoped usage (used / total) so the number line agrees with the bar,
  // which is driven by percentUsed (100 - budget percentRemaining). `used` can be fractional (it's
  // total - remaining, and remaining is a float), so floor it to whole credits consumed. Flooring —
  // not rounding — matches settings/copilot/features, which shows 0 until a full credit is used
  // (round would show "1 / 10,000" for 0.6 credits while settings shows "0 / 10,000").
  // NoLimit/UsageBlocked show gross monthly AI credits used (creditsUsed).
  const showsBudgetUsage = !isUnlimitedFamily && hasBudgetTotal && used !== null
  const showsGrossUsage = isUnlimitedFamily && creditsUsed !== null

  const usageText = showsGrossUsage
    ? `${formatNumber(creditsUsed)} AI credits used`
    : showsBudgetUsage
      ? `${formatNumber(Math.floor(used))} / ${formatNumber(creditsTotal)} AI credits`
      : null

  const progressBarColor = percentUsed >= 75 ? 'severe.emphasis' : 'accent.emphasis'

  // The unlimited family has no progress bar, so give the usage line extra room before the subtitle.
  const usageLineClassName =
    isUnlimitedFamily && subtitleLabel
      ? `${styles.tokenUsageDetails} ${styles.tokenUsageDetailsSpacious}`
      : styles.tokenUsageDetails

  return (
    <>
      {showDivider && <div className={styles.tokenUsageDivider} />}
      <div className={styles.tokenUsageSection}>
        <div className={headerClassName(isTopSection)}>
          <span className={styles.tokenUsageSectionTitle}>
            Monthly limit
            <UsageHintPopover
              title="Monthly limit"
              description="Usage limit per month, resets automatically. AI credit usage counts toward your monthly limit. Ask your account manager about increasing your overage when monthly limit is hit."
            />
          </span>
          {state === UserLevelBudgetState.UsageBlocked && (
            <Label size="small" variant="danger">
              Usage blocked
            </Label>
          )}
          <IconButton
            as="a"
            href={MANAGE_BUDGET_URL}
            icon={ArrowUpRightIcon}
            variant="invisible"
            size="small"
            aria-label="View usage"
            tooltipDirection="n"
          />
        </div>
        {!isUnlimitedFamily && (
          <ProgressBar
            progress={percentUsed}
            bg={progressBarColor}
            barSize="small"
            aria-label={`Monthly limit: ${Math.round(percentUsed)}% used`}
          />
        )}
        {usageText && (
          <div className={usageLineClassName}>
            <span className={styles.tokenUsageDetailMuted}>{usageText}</span>
          </div>
        )}
        {subtitleLabel && (
          <div className={styles.tokenUsageDetails}>
            <span className={styles.tokenUsageDetailMuted}>{subtitleLabel}</span>
          </div>
        )}
      </div>
    </>
  )
}

function SessionTokenUsageSection({
  sessionUsage,
  showDivider,
  isTopSection = false,
}: {
  sessionUsage: SessionTokenUsage
  showDivider: boolean
  isTopSection?: boolean
}) {
  return (
    <>
      {showDivider && <div className={styles.tokenUsageDivider} />}
      <div className={styles.tokenUsageSection}>
        <div className={headerClassName(isTopSection)}>
          <span className={styles.tokenUsageSectionTitle}>Session token usage</span>
          {sessionUsage.totalCredits !== null && (
            <span className={styles.tokenUsageSectionTitleBadge}>
              {`${sessionUsage.totalCredits.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} AI ${sessionUsage.totalCredits === 1 ? 'credit' : 'credits'}`}
            </span>
          )}
        </div>
        <div className={styles.sessionTokenGrid}>
          <span className={styles.tokenUsageDetail}>Input:</span>
          <span className={styles.tokenUsageDetailRight}>{sessionUsage.inputTokens.toLocaleString()} tokens</span>
          <span className={styles.tokenUsageDetail}>Output:</span>
          <span className={styles.tokenUsageDetailRight}>{sessionUsage.outputTokens.toLocaleString()} tokens</span>
        </div>
      </div>
    </>
  )
}
